import { serverURL } from "@/utils/serverURL";
import type {
  FlavorSpecCatalog,
  Recommendation,
} from "@/utils/types/flavor";

export async function fetchFlavorSpecCatalog(): Promise<FlavorSpecCatalog> {
  const res = await fetch(serverURL`/me/flavor/spec`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`flavor/spec failed: ${res.status}`);
  }
  return (await res.json()) as FlavorSpecCatalog;
}

// Wire-format of GET /me/flavor/reference-target-catalog. Mirrors the server
// shape in apps/nos/server/src/routes/me/flavor/referenceTargetCatalog.ts.
type ReferenceTargetCatalogPlan =
  | "PILOT"
  | "STANDARD"
  | "PRO"
  | "ENTERPRISE";

type ReferenceTargetCatalogItemRef = {
  ndbTable: string;
  rowId: number;
};

type ReferenceTargetCatalogFlavorCategory = {
  tag: string;
  mainCategory: string;
  subCategory: string;
  color: string;
};

type ReferenceTargetCatalogItem = {
  id: number;
  title: string;
  subtitle: string | null;
  flavorCategory: ReferenceTargetCatalogFlavorCategory | null;
  sortOrder: number;
  plan: ReferenceTargetCatalogPlan;
  locked: boolean;
  item: ReferenceTargetCatalogItemRef | null;
};

type ReferenceTargetCatalogResponse = {
  items: ReferenceTargetCatalogItem[];
};

function toLockReason(plan: ReferenceTargetCatalogPlan): string {
  switch (plan) {
    case "STANDARD":   return "Requires Standard plan";
    case "PRO":        return "Requires Pro plan";
    case "ENTERPRISE": return "Requires Enterprise plan";
    case "PILOT":      return "";
  }
}

export async function fetchReferenceTargetCatalog(): Promise<Recommendation[]> {
  const res = await fetch(serverURL`/me/flavor/reference-target-catalog`, {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`flavor/reference-target-catalog failed: ${res.status}`);
  }
  const body = (await res.json()) as ReferenceTargetCatalogResponse;

  // Map the server payload onto the Recommendation shape the picker UI
  // already consumes. Server rows arrive pre-sorted by sort_order.
  //
  // We keep `family: FamilyKey` as "neutral" — workbench code that derives
  // CSS vars like `var(--fam-${family})` only handles the closed FamilyKey
  // set, and catalog rows don't flow into those paths. The catalog card
  // reads `categoryTag` (display label) and `categoryColor` (pill swatch
  // hex) instead, which come from platform.flavor_category via JOIN.
  return body.items.map<Recommendation>((it) => {
    const base: Recommendation = {
      id: String(it.id),
      name: it.title,
      family: "neutral",
      note: it.subtitle ?? "",
      categoryTag: it.flavorCategory?.tag ?? null,
      categoryColor: it.flavorCategory?.color ?? null,
    };
    if (it.locked) {
      base.locked = true;
      base.lockReason = toLockReason(it.plan);
    }
    return base;
  });
}

// ────────────────────────────────────────────────────────────────────────
// Sidebar / Workbench — flavor list, search, star, hydration
// ────────────────────────────────────────────────────────────────────────

export type SidebarFlavorRow = {
  id: string;
  name: string;
  updatedAt: string;
};

export type RecentPage = {
  items: SidebarFlavorRow[];
  nextCursor: string | null;
  previousCursor: string | null;
};

export type StarredFlavorRow = SidebarFlavorRow & { starredAt: string };

export type SearchHit = SidebarFlavorRow & {
  description: string | null;
  isStarred: boolean;
};

export type HydratedSession = {
  id: string;
  label: string;
  name: string;
  description: string | null;
  isStarred: boolean;
  target: null;
  formulation: never[];
};

export type StarToggleResponse = {
  flavorId: string;
  isStarred: boolean;
};

export async function fetchRecentFlavors(
  cursor: string | null,
  direction: "after" | "before",
): Promise<RecentPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (direction !== "after") params.set("direction", direction);
  const qs = params.toString();
  const url = qs.length > 0
    ? serverURL`/me/flavor/recent?${qs}`
    : serverURL`/me/flavor/recent`;
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`flavor/recent failed: ${res.status}`);
  }
  return (await res.json()) as RecentPage;
}

export async function fetchStarredFlavors(): Promise<{ items: StarredFlavorRow[] }> {
  const res = await fetch(serverURL`/me/flavor/starred`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`flavor/starred failed: ${res.status}`);
  }
  return (await res.json()) as { items: StarredFlavorRow[] };
}

export async function searchFlavors(q: string): Promise<{ items: SearchHit[] }> {
  const res = await fetch(
    serverURL`/me/flavor/search?q=${encodeURIComponent(q)}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    throw new Error(`flavor/search failed: ${res.status}`);
  }
  return (await res.json()) as { items: SearchHit[] };
}

export async function fetchFlavorSession(id: string): Promise<HydratedSession> {
  const res = await fetch(serverURL`/me/flavor/${id}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`flavor/${id} failed: ${res.status}`);
  }
  return (await res.json()) as HydratedSession;
}

export async function starFlavor(id: string): Promise<StarToggleResponse> {
  const res = await fetch(serverURL`/me/flavor/${id}/star`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`flavor/${id}/star POST failed: ${res.status}`);
  }
  return (await res.json()) as StarToggleResponse;
}

/** Soft-delete a flavor (archive). Used to clean up a flavor whose full_cycle
 *  generation failed before redirecting the user back to New Flavor. */
export async function archiveFlavor(id: string): Promise<void> {
  const res = await fetch(serverURL`/me/flavor/${id}/archive`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`flavor/${id}/archive failed: ${res.status}`);
}

export async function unstarFlavor(id: string): Promise<StarToggleResponse> {
  const res = await fetch(serverURL`/me/flavor/${id}/star`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`flavor/${id}/star DELETE failed: ${res.status}`);
  }
  return (await res.json()) as StarToggleResponse;
}

// ────────────────────────────────────────────────────────────────────────
// Create flavor — POST /me/flavor
// ────────────────────────────────────────────────────────────────────────

export type CreateFlavorAnchor =
  | { mode: "catalog"; catalogId: number }
  | { mode: "chromatogram"; foodId: number }
  | { mode: "library"; ndbTable: "public.food" | "public.compound"; rowId: number }
  | { mode: "formulation"; foodId: number; userUploadedFormulaId: string };

export type CreateFlavorBasePreset =
  | { kind: "food"; id: number }
  | { kind: "flavor"; id: string }
  | { kind: "inventory"; id: string };

export type CreateFlavorAdvanced = {
  basePresets: CreateFlavorBasePreset[];
  inventoryOnly: boolean;
  regScope: string | null;
  claim: string | null;
  application: string | null;
  form: "liquid" | "powder" | null;
};

export type CreateFlavorBody = {
  path: "have-target" | "discover";
  description: string;
  anchor: CreateFlavorAnchor | null;
  advanced: CreateFlavorAdvanced;
};

export type CreateFlavorResponse = { flavorId: string };

export class CreateFlavorError extends Error {
  status: number;
  serverMessage: string | null;
  constructor(status: number, serverMessage: string | null) {
    super(serverMessage ?? `flavor create failed: ${status}`);
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

export async function createFlavor(
  body: CreateFlavorBody,
): Promise<CreateFlavorResponse> {
  const res = await fetch(serverURL`/me/flavor`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let serverMessage: string | null = null;
    try {
      const payload = (await res.json()) as { error?: unknown };
      if (typeof payload?.error === "string") serverMessage = payload.error;
    } catch {
      // ignore — non-JSON body
    }
    throw new CreateFlavorError(res.status, serverMessage);
  }
  return (await res.json()) as CreateFlavorResponse;
}

// ────────────────────────────────────────────────────────────────────────
// Import formulation → pre-create target (parse + finalize)
// ────────────────────────────────────────────────────────────────────────

export type ImportParseSource =
  | { kind: "paste"; text: string }
  | { kind: "file"; fileUploadId: string };

// A substance the import row resolved to. Same wire vocabulary as the server
// (compound → kind "compound", food → kind "mixture"); ndbTable + rowId map
// straight onto FinalizeFormulationBody.rows[].{resolved_ndb_table, resolved_row_id}.
export type ResolvedSubstance = {
  ndbTable: "public.food" | "public.compound";
  rowId: number;
  name: string;
  kind: "compound" | "mixture";
  cas: string | null;
  // FEMA fallback identifier (compounds without a CAS). Optional: the picker
  // search endpoint doesn't return it, only the parse/resolve path does.
  fema?: string | null;
};

export type ImportResolvedRow = {
  raw: string | null;
  name: string | null;
  cas: string | null;
  fema: string | null;
  // ppm is null when the dose was un-convertible (volumetric/molar/unknown) or
  // no amount was parsed — the user must supply a ppm value.
  ppm: number | null;
  // Always "ppm" now (server normalizes); kept for wire compatibility.
  unit: string;
  // "ok" when ppm is a usable conversion; "unconvertible" when the source unit
  // can't be interpreted (volumetric/molar/unknown) and a dose must be entered.
  doseStatus: "ok" | "unconvertible";
  rawValue: number | null;
  rawUnit: string | null;
  resolved: ResolvedSubstance | null;
};

/** Flavor-agnostic substance search for the import candidate picker. */
export async function searchImportSubstances(
  q: string,
  kind: "all" | "compound" | "food" = "all",
  signal?: AbortSignal,
): Promise<{ items: ResolvedSubstance[] }> {
  const res = await fetch(
    serverURL`/me/flavor/import/search?q=${encodeURIComponent(q)}&kind=${kind}`,
    { credentials: "include", signal },
  );
  if (!res.ok) throw new Error(`import/search failed: ${res.status}`);
  return (await res.json()) as { items: ResolvedSubstance[] };
}

/** Resolve a pasted/uploaded formulation into raw → resolved-ingredient rows. */
export async function parseImportFormulation(
  source: ImportParseSource,
): Promise<{ rows: ImportResolvedRow[] }> {
  const res = await fetch(serverURL`/me/flavor/import/parse`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(source),
  });
  if (!res.ok) throw new Error(`import/parse failed: ${res.status}`);
  return (await res.json()) as { rows: ImportResolvedRow[] };
}

export type FinalizeFormulationBody = {
  name: string;
  rows: {
    raw?: string | null;
    resolved_ndb_table: "public.food" | "public.compound";
    resolved_row_id: number;
    ppm: number;
    // Per-row unit (free-form, default ppm) → persisted → worker raw_concentration_unit.
    unit?: "ppm" | "mg/kg";
  }[];
  source_file_upload_id?: string | null;
};

/** Persist the confirmed formulation as user_uploaded_formula + public.food. */
export async function finalizeFormulation(
  body: FinalizeFormulationBody,
): Promise<{ food_id: number; user_uploaded_formula_id: string }> {
  const res = await fetch(serverURL`/me/flavor/import/finalize-formulation`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`import/finalize-formulation failed: ${res.status}`);
  return (await res.json()) as {
    food_id: number;
    user_uploaded_formula_id: string;
  };
}
